/**
 * useDuviCoreAdminAppShell — the app-shell's BEHAVIOUR half (architecture.md
 * §6.1). Owns everything data/session-driven so the shell view stays a pure
 * composition of @duvi-agency/ui shell components: the live notifications feed
 * + its read mutations, the current-user profile for the top-right user menu,
 * logout, and the light/dark mode binding. UI-only state (collapse,
 * mobile-drawer open) stays in the view.
 */

import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import { formatDistanceToNow } from "date-fns";
import { useDuviSharedAdminAuth } from "@duvi-agency/auth";
import { useDuviSharedAdminTheme } from "@duvi-agency/theme";
import type {
  DuviSharedAdminNotificationItem,
  DuviSharedAdminSidebarGroup,
} from "@duvi-agency/ui";
import {
  me,
  notifications as notificationsApi,
  type Notification,
} from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";

// ── Nav structure ────────────────────────────────────────────────────────────
// key doubles as the `activeMenuItem` identity; icon is a DS Icon name.
interface NavEntry {
  key: string;
  label: string;
  icon: string;
  path: string;
}

const NAV_SECTIONS: { label: string; items: NavEntry[] }[] = [
  {
    label: "OVERVIEW",
    items: [
      {
        key: "Dashboard",
        label: "Dashboard",
        icon: "dashboard",
        path: "/dashboard",
      },
    ],
  },
  {
    label: "OPERATIONS",
    items: [
      {
        key: "Knowledge Base",
        label: "Knowledge base",
        icon: "inbox",
        path: "/knowledge-base",
      },
      {
        key: "Support",
        label: "Support",
        icon: "message",
        path: "/support/tickets",
      },
    ],
  },
  {
    label: "PLATFORM",
    items: [
      { key: "Niches", label: "Niches", icon: "box", path: "/platform/niches" },
      { key: "Users", label: "Users", icon: "users", path: "/user-management" },
      {
        key: "Audit Log",
        label: "Audit log",
        icon: "activity",
        path: "/audit-log",
      },
    ],
  },
];

const PATH_TO_KEY: Array<[string, string]> = [
  ["/dashboard", "Dashboard"],
  ["/knowledge-base", "Knowledge Base"],
  ["/support", "Support"],
  ["/platform/niches", "Niches"],
  ["/user-management", "Users"],
  ["/audit-log", "Audit Log"],
];

function detectActiveKey(pathname: string): string {
  for (const [prefix, key] of PATH_TO_KEY) {
    if (pathname.startsWith(prefix)) return key;
  }
  return "";
}

function apiNotifToItem(n: Notification): DuviSharedAdminNotificationItem {
  let time: string | undefined;
  try {
    time = formatDistanceToNow(new Date(n.created_at), { addSuffix: true });
  } catch {
    time = n.created_at;
  }
  return {
    id: n.notif_id,
    title: n.title,
    description: n.summary,
    time,
    unread: !n.read,
  };
}

export interface UseDuviCoreAdminAppShell {
  navGroups: DuviSharedAdminSidebarGroup[];
  navSections: { label: string; items: NavEntry[] }[];
  resolvedActiveKey: (explicit?: string) => string;
  navigateToKey: (key: string) => void;
  notifItems: DuviSharedAdminNotificationItem[];
  handleNotifClick: (id: string) => void;
  handleMarkAllRead: () => void;
  profileName: string;
  handleLogout: () => void;
  handleGoToSettings: () => void;
  mode: "light" | "dark";
  toggleMode: () => void;
}

export function useDuviCoreAdminAppShell(
  buildIcon: (name: string) => React.ReactNode,
): UseDuviCoreAdminAppShell {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, authClient } = useDuviSharedAdminAuth();
  const { mode, toggleMode } = useDuviSharedAdminTheme();

  const { data: profile } = useDuviCoreAdminQuery(() => me.get(), []);
  const { data: notifData, refetch: refetchNotifs } = useDuviCoreAdminQuery(
    () => notificationsApi.list(),
    [],
    { pollInterval: 10 * 60 * 1000 },
  );

  const notifItems = useMemo(
    () => (notifData ?? []).map(apiNotifToItem),
    [notifData],
  );

  const navGroups = useMemo<DuviSharedAdminSidebarGroup[]>(
    () =>
      NAV_SECTIONS.map((section) => ({
        label: section.label,
        items: section.items.map((item) => ({
          key: item.key,
          label: item.label,
          icon: buildIcon(item.icon),
        })),
      })),
    [buildIcon],
  );

  const navigateToKey = (key: string) => {
    const entry = NAV_SECTIONS.flatMap((s) => s.items).find(
      (i) => i.key === key,
    );
    if (entry) navigate(entry.path);
  };

  const resolvedActiveKey = (explicit?: string) =>
    explicit ?? detectActiveKey(location.pathname);

  const handleNotifClick = (id: string) => {
    notificationsApi
      .markRead(id)
      .then(() => refetchNotifs())
      .catch(() => {});
  };

  const handleMarkAllRead = () => {
    const unread = (notifData ?? []).filter((n) => !n.read);
    Promise.all(unread.map((n) => notificationsApi.markRead(n.notif_id)))
      .then(() => refetchNotifs())
      .catch(() => {});
  };

  const handleLogout = () => {
    void (async () => {
      try {
        await authClient.logout();
      } finally {
        logout();
        // Hard-reload to the SPA root — see bug 0054.
        window.location.href = "/";
      }
    })();
  };

  const handleGoToSettings = () => navigate("/settings");

  return {
    navGroups,
    navSections: NAV_SECTIONS,
    resolvedActiveKey,
    navigateToKey,
    notifItems,
    handleNotifClick,
    handleMarkAllRead,
    profileName: profile?.name ?? "Platform admin",
    handleLogout,
    handleGoToSettings,
    mode,
    toggleMode,
  };
}
